import { FC } from 'react';
import {
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { Divider, Stack } from '@mui/material';
import { APP } from 'config';
import {
  DateRangeFilter,
  FilterButton,
  Filters,
  OpenSearchFilter,
  SelectFilter,
  SuppliersFilter,
} from 'components/filters';
import moment from 'moment';
import { formatCountry, formatList } from 'utils/format';
import { shopItemCategories } from 'types/MarketplaceItems';
import { DownloadXlsAction } from 'components/actions';
import {
  discountPackages,
  discountsWithStatsFiltersSchema,
  useBulkUpdateDiscounts,
  useDiscountsWithStats,
  useExportToXlsDiscountsWithStats,
} from 'store/discounts';
import { useSearchParams } from 'utils/navigation';
import { usePredefinedToasts } from 'utils/Toast';

export const DiscountsUsage: FC = () => {
  const { t, i18n } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const { selectedRowIds, setSelectedRowIds } = Table.useSelectedRows();

  const { searchParams: filters, navigate } = useSearchParams(
    discountsWithStatsFiltersSchema,
  );

  const { data: discounts, isLoading: isDiscountsLoading } =
    useDiscountsWithStats(filters);

  const { mutate: bulkUpdate, isLoading: isUpdating } =
    useBulkUpdateDiscounts();
  const { mutate: exportToXls, isLoading: isExportingToXls } =
    useExportToXlsDiscountsWithStats();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <ListTotal total={discounts?.total} />
          <Stack direction="row" alignItems="center" gap={1}>
            <Filters
              value={filters}
              initialValues={discountsWithStatsFiltersSchema.parse({})}
              onChange={(v) => navigate({ params: { ...v, page: 1 } })}
            >
              {({ value, initialValues, setFilter }) => (
                <>
                  <FilterButton
                    label={t('discounts.type.label')}
                    value={value.package}
                    initialValue={initialValues.package}
                    onChange={(v) => setFilter('package', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        name="package"
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        multiple
                        searchable={false}
                        options={discountPackages.map((pack) => ({
                          key: pack,
                          label: t(`discounts.package.${pack}`),
                        }))}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('marketplaceItems.supplier')}
                    value={value.suppliers}
                    initialValue={initialValues.suppliers}
                    onChange={(v) => setFilter('suppliers', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SuppliersFilter
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t(
                      'recognition.allowanceEmployeeOverviewListValidFrom',
                    )}
                    value={value.validAt}
                    initialValue={initialValues.validAt}
                    onChange={(v) => setFilter('validAt', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <DateRangeFilter
                        value={value}
                        onApply={applyFilter}
                        onClear={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('benefits.country')}
                    value={value.countries}
                    initialValue={initialValues.countries}
                    onChange={(v) => setFilter('countries', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        name="countries"
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        multiple
                        options={APP.countries.alpha3.map((alpha3) => ({
                          key: alpha3,
                          label: formatCountry(i18n.language, alpha3),
                        }))}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('benefits.benefit_type')}
                    value={value.categories}
                    initialValue={initialValues.categories}
                    onChange={(v) => setFilter('categories', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        name="categories"
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        multiple
                        options={shopItemCategories.map((category) => ({
                          key: category,
                          label: t(`marketplaceItems.itemCategory-${category}`),
                        }))}
                      />
                    )}
                  </FilterButton>

                  <OpenSearchFilter
                    value={value.search}
                    onApplyFilter={(v) => setFilter('search', v)}
                    placeholder={t('common.search')}
                  />
                </>
              )}
            </Filters>

            <Divider orientation="vertical" variant="middle" flexItem />

            <DownloadXlsAction
              listLength={discounts?.total ?? 0}
              exportToExcel={() => exportToXls({ filters })}
              loading={isExportingToXls}
            />
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={discounts?.items ?? []}
        isLoading={isDiscountsLoading}
        columns={[
          {
            id: 'name',
            header: t('common.name'),
            cell: ({ row: { original: discount } }) => (
              <Stack spacing={0.25} alignItems="flex-start">
                <Typography>{discount.name}</Typography>
                {discount.isEnabled ? null : (
                  <Typography color="error">{t('status.disabled')}</Typography>
                )}
              </Stack>
            ),
          },
          {
            id: 'package',
            header: t('discounts.type.label'),
            cell: ({ row: { original: discount } }) =>
              formatList(
                i18n.language,
                discount.packages.map((pack) => t(`discounts.package.${pack}`)),
              ),
          },
          {
            id: 'supplier',
            header: t('marketplaceItems.supplier'),
            cell: ({ row: { original: discount } }) =>
              discount.supplier?.name ?? null,
          },
          {
            id: 'validTo',
            header: t('recognition.allowanceEmployeeOverviewListValidTo'),
            cell: ({ row: { original: discount } }) =>
              discount.validTo ? (
                <Typography
                  color={
                    moment(discount.validTo).isBefore()
                      ? 'error'
                      : 'textPrimary'
                  }
                >
                  {moment(discount.validTo).format('l')}
                </Typography>
              ) : null,
            meta: { sort: true },
          },
          {
            id: 'isFavourites',
            header: t('discounts.favorites.label'),
            cell: ({ row: { original: discount } }) => discount.favoritesCount,
            meta: { sort: true, align: 'right' },
          },
          {
            id: 'views',
            header: t('common.total_views'),
            cell: ({ row: { original: discount } }) => discount.viewsCount,
            meta: { sort: true, align: 'right' },
          },
          {
            id: 'uniqueViews',
            header: t('common.unique_views'),
            cell: ({ row: { original: discount } }) =>
              discount.uniqueViewsCount,
            meta: { sort: true, align: 'right' },
          },
        ]}
        sort={{
          value: filters.sort,
          onChange: (v) => navigate({ params: { sort: v } }),
        }}
        pagination={
          discounts && {
            count: discounts.total,
            page: filters.page,
            pageSize: filters.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
        selection={{
          value: selectedRowIds,
          onChange: setSelectedRowIds,
          actions: [
            {
              label: t('discounts.actions.enable'),
              disabled: isUpdating,
              onClick: () =>
                bulkUpdate(
                  { enabled: Object.keys(selectedRowIds) },
                  {
                    onSuccess: () => {
                      predefinedToasts.success.updated();
                      setSelectedRowIds({});
                    },
                  },
                ),
            },
            {
              label: t('discounts.actions.disable'),
              disabled: isUpdating,
              onClick: () =>
                bulkUpdate(
                  { disabled: Object.keys(selectedRowIds) },
                  {
                    onSuccess: () => {
                      predefinedToasts.success.updated();
                      setSelectedRowIds({});
                    },
                  },
                ),
            },
          ],
        }}
      />
    </TableWrapper>
  );
};
